import { lazy, useEffect } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FileQuestion } from 'lucide-react';

import { UNAUTHORIZED_EVENT } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { ProfileProvider } from '@/lib/profile';
import { AppShell } from '@/components/layout/app-shell';
import { ErrorBoundary } from '@/components/error-boundary';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import LoginPage from '@/features/auth/LoginPage';
import SignupPage from '@/features/auth/SignupPage';
import ForgotPasswordPage from '@/features/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/features/auth/ResetPasswordPage';
import RequireAuth from '@/features/auth/RequireAuth';

/**
 * Feature screens are lazy so the initial bundle over LAN wifi stays small, and
 * so a failure to load one route does not take the shell with it.
 *
 * These modules belong to the other three web agents. The shell imports their
 * default exports and nothing else; if one is missing, the boundary below shows
 * an error on that route instead of a white screen everywhere.
 */
const TodayPage = lazy(() => import('@/features/today/TodayPage'));
const SchedulePage = lazy(() => import('@/features/today/SchedulePage'));
// web-meds landed its three route components under `pages/` rather than at the
// feature root. Importing the real path rather than the one UI_KIT implies,
// because a route that cannot resolve is a blank app.
const MedicationsPage = lazy(() => import('@/features/medications/pages/MedicationsPage'));
const MedicationDetailPage = lazy(
  () => import('@/features/medications/pages/MedicationDetailPage'),
);
const SettingsPage = lazy(() => import('@/features/medications/pages/SettingsPage'));
const HistoryPage = lazy(() => import('@/features/history/HistoryPage'));

/**
 * The one place a 401 is handled (UI_KIT: "Do not handle 401 in feature code").
 *
 * `lib/api.ts` dispatches `medtrack:unauthorized` when any request comes back
 * 401; this listens once, clears the cached session so RequireAuth cannot let a
 * stale success through, and sends the user to /login remembering where they
 * were. The guard against redirecting while already on an auth screen is what
 * stops the login page's own session probe from looping.
 */
/** Signed-out screens. A 401 while on one of these is expected, not a redirect. */
const AUTH_SCREENS = new Set(['/login', '/signup', '/forgot-password', '/reset-password']);

function UnauthorizedRedirect(): null {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    const onUnauthorized = (): void => {
      const path = window.location.pathname;
      if (AUTH_SCREENS.has(path)) return;

      // Drop the cached session outright. Leaving a stale success in the cache
      // would let RequireAuth wave the user straight back into the app.
      queryClient.removeQueries({ queryKey: qk.session() });
      navigate('/login', {
        replace: true,
        state: { from: `${location.pathname}${location.search}` },
      });
    };

    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, [navigate, location.pathname, location.search, queryClient]);

  return null;
}

function NotFoundPage(): JSX.Element {
  return (
    <EmptyState
      icon={FileQuestion}
      title="Page not found"
      description="That address does not match anything in this app."
      action={
        <Button asChild variant="secondary">
          <Link to="/">Go to Today</Link>
        </Button>
      }
    />
  );
}

/**
 * Route table, per UI_KIT:
 *
 *   /login, /signup            web-shell
 *   /                          web-today      Today
 *   /schedule                  web-today      upcoming agenda
 *   /medications               web-meds       list
 *   /medications/:id           web-meds       detail
 *   /settings                  web-meds       profile, sharing, theme, password
 *   /history                   web-history    log, filters, charts
 */
export function AppRoutes(): JSX.Element {
  const location = useLocation();

  return (
    <>
      <UnauthorizedRedirect />
      {/* Keyed on pathname so a crashed route clears when the user navigates
          away, instead of pinning the error until a full reload. */}
      <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route
            element={
              <RequireAuth>
                <ProfileProvider>
                  <AppShell />
                </ProfileProvider>
              </RequireAuth>
            }
          >
            <Route index element={<TodayPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/medications" element={<MedicationsPage />} />
            <Route path="/medications/:id" element={<MedicationDetailPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            {/* Anything else, for a signed-in user, is a 404 inside the shell
                rather than a redirect that swallows the typo silently. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </ErrorBoundary>
    </>
  );
}

export default AppRoutes;
